// components/FittingsPanel.js

import React, { useState } from 'react';
import { Box, Button, Typography, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { styled } from '@mui/system';
import fittingsData from './fittingsData'; // Убедитесь, что путь к файлу верный

const ScrollableContent = styled(Box)({
  overflowY: 'auto', // Прокрутка только для содержимого
  flex: 1,
  // Стилизация полосы прокрутки
  '&::-webkit-scrollbar': {
    width: '0.5rem', // 8px
  },
  '&::-webkit-scrollbar-track': {
    background: '#f5f5f5',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#d0d0d0',
    borderRadius: '0.25rem', // 4px
    '&:hover': {
      background: '#b0b0b0',
    },
  },
});

function FittingsPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  // Получаем категории из fittingsData
  const categories = [...new Set(Object.values(fittingsData).map(fitting => fitting.category))];

  // Получаем подкатегории для выбранной категории
  const getSubcategories = (category) => {
    const fittings = Object.values(fittingsData).filter(f => f.category === category);
    const subcategories = [...new Set(fittings.map(f => f.subcategory).filter(Boolean))];
    return subcategories;
  };

  // Проверяем, есть ли подкатегории у категории
  const hasSubcategories = (category) => {
    return getSubcategories(category).length > 0;
  };

  // Получаем фитинги по категории и подкатегории
  const getFittingsBySubcategory = (category, subcategory) => {
    return Object.keys(fittingsData).filter(
      key => fittingsData[key].category === category && fittingsData[key].subcategory === subcategory
    );
  };

  // Получаем фитинги без подкатегорий
  const getFittingsWithoutSubcategory = (category) => {
    return Object.keys(fittingsData).filter(
      key => fittingsData[key].category === category && !fittingsData[key].subcategory
    );
  };

  return (
    <Box
      sx={{
        width: '13.75rem',
        padding: '1rem',
        boxSizing: 'border-box',
        borderLeft: '0.0625rem solid #e0e0e0',
        borderTop: '0.0625rem solid #e0e0e0',
        // Если открыто - фиксированная высота с прокруткой
        ...(isOpen ? {
          maxHeight: '50vh', // Максимум 50% высоты экрана
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        } : {
          height: 'auto',
        }),
      }}
    >
      <Typography variant="h6" gutterBottom sx={{ color: '#212121', fontWeight: 600 }}>
        Фитинги
      </Typography>
      <Button
        variant="contained"
        fullWidth
        sx={{
          backgroundColor: isOpen ? '#d32f2f' : '#f5f5f5',
          color: isOpen ? '#ffffff' : '#424242',
          fontWeight: 500,
          border: isOpen ? '0.0625rem solid #d32f2f' : '0.0625rem solid #e0e0e0',
          boxShadow: 'none',
          mb: 1,
          '&:hover': {
            backgroundColor: isOpen ? '#c62828' : '#e0e0e0',
            border: isOpen ? '0.0625rem solid #c62828' : '0.0625rem solid #d0d0d0',
            boxShadow: 'none',
          }
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? 'Скрыть фитинги' : 'Показать фитинги'}
      </Button>

      {isOpen && (
        <>
          {/* Кнопка "Назад" - фиксированная */}
          {(selectedCategory || selectedSubcategory) && (
            <Button
              variant="text"
              sx={{ mb: 1 }}
              onClick={() => {
                if (selectedSubcategory) {
                  setSelectedSubcategory(null);
                } else {
                  setSelectedCategory(null);
                }
              }}
            >
              {selectedSubcategory ? 'Назад к подкатегориям' : 'Назад к категориям'}
            </Button>
          )}
          
          {/* Прокручиваемый контент */}
          <ScrollableContent>
            {!selectedCategory ? (
              // Показываем список категорий
              <List>
                {categories.map((category) => (
                  <ListItem key={category} disablePadding>
                    <ListItemButton onClick={() => setSelectedCategory(category)}>
                      <ListItemText primary={category} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : !selectedSubcategory && hasSubcategories(selectedCategory) ? (
              // Показываем подкатегории, если они есть
              <List>
                {getSubcategories(selectedCategory).map((subcategory) => (
                  <ListItem key={subcategory} disablePadding>
                    <ListItemButton onClick={() => setSelectedSubcategory(subcategory)}>
                      <ListItemText primary={subcategory} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            ) : (
              // Показываем фитинги
              <List>
                {(selectedSubcategory
                  ? getFittingsBySubcategory(selectedCategory, selectedSubcategory)
                  : getFittingsWithoutSubcategory(selectedCategory)
                ).map((fittingKey) => (
                  <ListItem key={fittingKey} disablePadding>
                    <ListItemButton
                      draggable
                      style={{ cursor: 'grab' }}
                      onDragStart={(e) => {
                        e.dataTransfer.setData('fittingKey', fittingKey);
                        e.dataTransfer.effectAllowed = 'copyMove';
                      }}
                    >
                      <ListItemText primary={fittingsData[fittingKey].displayName} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </ScrollableContent>
        </>
      )}
    </Box>
  );
}

export default FittingsPanel;
